import { UserCreationDto, UserCreationExtendDto, UsersUpdateDto } from '../dtos/user.dto';
import {
  BaseUser, Manager, User, UserCreation, UserCreationExtend,
  UserShortCreation,
  UsersUpdate,
} from '../models/user';
import { USER_ACCOUNT_STATUS } from '../models/user-account-status';
import { USER_TYPE_READABLE } from '../models/user-type';
import { extractValidationError } from '../utils/extractValidationError';
import { dateMapper } from './date.mapper';
import { registrationStatsMapper } from './registration-stats.mapper';

const MAP_USER_TYPE_FROM_DTO = {
  Client: USER_TYPE_READABLE.CLIENT,
  Instructor: USER_TYPE_READABLE.INSTRUCTOR,
  Staff: USER_TYPE_READABLE.STAFF,
  Manager: USER_TYPE_READABLE.MANAGER,
  Admin: USER_TYPE_READABLE.ADMIN,
  Elearning_Manager: USER_TYPE_READABLE.ELEARNING_MANAGER,
  Elearning_Student: USER_TYPE_READABLE.ELEARNING_STUDENT,
};

const MAP_USER_ACCOUNT_STATUS_FROM_DTO = {
  Y: USER_ACCOUNT_STATUS.ACTIVE,
  N: USER_ACCOUNT_STATUS.IN_ACTIVE,
};

/**
 * Map to base user.
 * @param {object} dto Dto.
 * @returns {BaseUser}
 */
function mapBaseUserFromDto(dto) {
  return new BaseUser({
    id: dto.id,
    firstName: dto.first_name,
    lastName: dto.last_name,
  });
}

/**
 * Map to manager.
 * @param {object} dto Dto.
 * @returns {Manager}
 */
function mapManagerFromDto(dto) {
  return new Manager({
    ...mapBaseUserFromDto(dto),
    hasPreBoughtLaminatedCard: dto.has_pre_bought_laminated_card,
  });
}

/**
 * Map to user.
 * @param {object} dto Dto.
 * @returns {User}
 */
function fromDto(dto) {
  return new User({
    ...mapBaseUserFromDto(dto),
    middleName: dto.middle_name,
    jobTitle: dto.job_title,
    dob: dto.dob,
    isCorporateStudent: dto.is_corporate_student,
    isInNOutAdmin: dto.is_in_n_out_admin,
    language: dto.language,
    userName: dto.username,
    homeNumber: dto.home_number,
    workNumber: dto.work_number,
    faxNumber: dto.fax_number,
    email: dto.email,
    createdDate: dateMapper.fromDto(dto.create_date),
    managerId: dto.manager_id,
    isNewSystem: dto.is_new_system === 1,
    isVisible: dto.visible,
    rbsServerId: dto.rbs_server_id,
    studentsCount: dto.students_count,
    manager: dto.manager ? mapManagerFromDto(dto.manager) : null,
    type: dto.type ? {
      ...dto.type,
      name: MAP_USER_TYPE_FROM_DTO[dto.type.name],
    } : null,
    active: MAP_USER_ACCOUNT_STATUS_FROM_DTO[dto.active],
    group: dto.group ? {
      id: dto.group.id,
      name: dto.group.name,
      workNumber: dto.group.work_number,
      email: dto.group.email,
      addressId: dto.group.address_id,
      size: dto.group.size,
      businessLicenseNumber: dto.group.business_license_number,
      createdDate: dateMapper.fromDto(dto.group.create_date),
      editedDate: dateMapper.fromDto(dto.group.edit_date),
      active: dto.group.active,
    } : null,
    groupId: dto.group_id,
    hasPreBoughtLaminatedCard: dto.has_pre_bought_laminated_card,
    address: dto.address ? {
      id: dto.address.id,
      street: dto.address.street,
      street2: dto.address.street2,
      city: dto.address.city,
      stateCode: dto.address.state_code,
      zip: dto.address.zip,
      region: dto.address.region,
      county: dto.address.county,
    } : null,
    registrationStats: dto.registration_stats ? registrationStatsMapper.fromDto(dto.registration_stats) : null,
    reminder: dto.reminder ? {
      id: dto.reminder.id,
      userId: dto.reminder.user_id,
      certificateRenewal: dto.reminder.certificate_renewal,
      inPersonClass: dto.reminder.in_person_class,
      smsCertificateRenewal: dto.reminder.sms_certificate_renewal,
    } : null,
  });
}

/**
 * Map user to creation data.
 * @param {UserCreation} data Data to map.
 * @returns {UserCreationDto}
 */
function toCreationDto(data) {
  return new UserCreationDto({
    first_name: data.firstName,
    middle_name: data.middleName,
    last_name: data.lastName,
    job_title: data.jobTitle,
    group_id: data.groupId,
    hasPreBoughtLaminatedCard: data.hasPreBoughtLaminatedCard,
    email: data.email,
    phone_number: data.phoneNumber,
    additional_phone_number: data.additionalPhoneNumber,
    street: data.street,
    street2: data.street2,
    city: data.city,
    state_code: data.stateCode,
    zip_code: data.zip,
    dob: dateMapper.toDto(data.dob),
    username: data.username,
    language_id: data.languageId?.toString(),
    rbs_server_id: data.rbsServerId,
    company_name: data.companyName,
  });
}

/**
 * Map user to extend creation data.
 * @param {UserCreationExtend} data Data to map.
 * @returns {UserCreationExtendDto}
 */
function toCreationExtendDto(data) {
  return new UserCreationExtendDto({
    username: data.username,
    password: data.password,
    password_confirmation: data.confirmPassword,
    first_name: data.firstName,
    middle_name: data.middleName,
    last_name: data.lastName,
    job_title: data.jobTitle,
    group_name: data.groupName,
    email: data.email,
    dob: dateMapper.toDto(data.dob),
    email_confirmation: data.confirmEmail,
    phone_number: data.phoneNumber,
    street: `${data.mailingAddress} ${data.suite ?? ''}`,
    city: data.city,
    state: data.stateCode,
    zip_code: data.zip,
    employee_id: data.employeeId ?? undefined,
    store_number: data.storeNumber ?? undefined,
    corporate_code: data.corporateCode,
  });
}

/**
 * Map to users update data.
 * @param {UsersUpdate[]} usersUpdateData Data to map.
 * @returns {object}
 */
function toUsersUpdateDto(usersUpdateData) {
  return {
    update_data: usersUpdateData.map(data => new UsersUpdateDto({ user_id: data.userId, dob: dateMapper.toDto(data.dateOfBirth) })),
  };
}

/**
 * Map error from dto to model.
 * @param {object} errorDto error dto.
 * @returns {UserCreation | UserShortCreation}
 */
function validationErrorFromDto(errorDto) {
  return {
    firstName: extractValidationError(errorDto.first_name),
    middleName: extractValidationError(errorDto.middle_name),
    lastName: extractValidationError(errorDto.last_name),
    jobTitle: extractValidationError(errorDto.job_title),
    groupId: extractValidationError(errorDto.group_id),
    email: extractValidationError(errorDto.email),
    phoneNumber: extractValidationError(errorDto.phone_number),
    additionalPhoneNumber: extractValidationError(errorDto.additional_phone_number),
    street: extractValidationError(errorDto.street),
    city: extractValidationError(errorDto.city),
    stateCode: extractValidationError(errorDto.state_code),
    zip: extractValidationError(errorDto.zip),
    dob: extractValidationError(errorDto.dob),
    username: extractValidationError(errorDto.username),
  };
}

export const userMapper = {
  fromDto,
  toCreationDto,
  validationErrorFromDto,
  mapBaseUserFromDto,
  toCreationExtendDto,
  toUsersUpdateDto,
};
